import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilState } from "recoil";

import { errors, RideCrudModeKey } from "../constants";
import type { IExtendableDto, SimpleResult } from "../dto";
import { EditFormMode, type DirectionEnum } from "../enums";
import { Log, resolveCrudEditDrawerAtomKey } from "../helpers";
import {
  DrawerType,
  type DrawerProps,
  type ICoursesCalendarSlotDto,
  type ICourseScheduleMetricItem,
  type IDrawer,
  type RydeUiUnit,
  type TCanResolve,
  type TDrawerOnCreateAction,
} from "../models";
import {
  drawersAtom,
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  loadingEditFormContentAtomFamily,
} from "../recoil";
import { useHasPermissionsCheck } from "./use-has-permissions-check";

export const useDrawers = () => {
  const [drawers, setDrawers] = useRecoilState(drawersAtom);

  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const { t } = useTranslation();

  const clearCrudEditDrawerAtomsBeforeOpen = useRecoilCallback(
    ({ set }) =>
      (props: DrawerProps<DrawerType>): boolean => {
        const key = resolveCrudEditDrawerAtomKey(props);
        if (!key) {
          return false;
        }
        const [entity, mode] = key;
        set(editFormDataAF(key), undefined);
        set(editFormDataInitialSnapshotAF(key), undefined);
        set(loadingEditFormContentAtomFamily(entity), false);
        Log.debug(
          `useDrawers: cleared/prepared edit-form atoms before drawer reopen entity=${entity} mode=${mode} key=${key}`,
        );
        return true;
      },
    [],
  );

  const setDropdownAddDrawerDraft = useRecoilCallback(
    ({ set }) =>
      ({
        entity,
        draft,
      }: {
        entity: RydeUiUnit;
        draft: Partial<IExtendableDto> | undefined;
      }): void => {
        set(editFormDataAF([entity, EditFormMode.DropdownAddDrawer]), draft);
      },
    [],
  );

  const openDrawer = useCallback(
    <T extends DrawerType>({ drawerProps }: IDrawer<T>): void => {
      if ("entity" in drawerProps) {
        const hasMinimumReadPermission = hasPermissionsCheck({
          entity: drawerProps?.entity,
          action: "read",
        });

        if (!hasMinimumReadPermission) {
          Log.errorAndToast(t(errors.common.dashboardUnauthenticatedCardError));
          return;
        }
      }
      clearCrudEditDrawerAtomsBeforeOpen(
        drawerProps as DrawerProps<DrawerType>,
      );

      setDrawers(oldDialogs => {
        const newDrawerProps = {
          ...drawerProps,
          drawerNumber: oldDialogs.length,
        };
        return [...oldDialogs, { drawerProps: newDrawerProps }];
      });
    },
    [clearCrudEditDrawerAtomsBeforeOpen, hasPermissionsCheck, setDrawers, t],
  );

  const openAsyncDrawer = useEvent(
    async <T extends DrawerType>({
      drawerProps,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }: IDrawer<T>): Promise<SimpleResult<any>> => {
      return new Promise(resolve => {
        openDrawer({
          drawerProps: {
            ...drawerProps,
            resolveFn: resolve,
          },
        });
      });
    },
  );

  const openEditFormDrawer = useEvent(
    async ({
      entity,
      id,
      idCopyFrom,
      mode = EditFormMode.UnboundDrawer,
      onCloseAction,
      canResolve,
    }: {
      entity: RydeUiUnit;
      id: number | undefined;
      idCopyFrom?: number;
      mode?: EditFormMode;
      onCloseAction?: () => void;
      canResolve?: TCanResolve;
    }): Promise<SimpleResult<IExtendableDto>> => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.EditForm,
          id,
          entity,
          onCloseCustomAction: onCloseAction,
          canResolve,
          idCopyFrom,
          mode,
          subTitle: "", // "**" DO NOT REMOVE!. Demand may return.
        },
      });
    },
  );

  const openAddDropdownFormDrawer = useEvent(
    async ({
      entity,
      idCopyFrom,
      canResolve,
      newItemDraft,
    }: {
      entity: RydeUiUnit;
      idCopyFrom?: number | undefined;
      canResolve?: TCanResolve;
      /** What a new record starts with, over the fields' `newItemValue` defaults. */
      newItemDraft?: Partial<IExtendableDto>;
    }): Promise<SimpleResult<unknown>> => {
      if (newItemDraft) {
        setDropdownAddDrawerDraft({ entity, draft: newItemDraft });
      }

      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.EditForm,
          entity,
          idCopyFrom,
          canResolve,
          mode: EditFormMode.DropdownAddDrawer,
          subTitle: "", // "*"  DO NOT REMOVE!. Demand may return.
          // Only a save clears the form data; after a cancel the next drawer of the entity would open on the draft.
          onCloseCustomAction: newItemDraft
            ? () => setDropdownAddDrawerDraft({ entity, draft: undefined })
            : undefined,
        },
      });
    },
  );

  const openNewOrDuplicateDrawer = useEvent(
    async ({
      entity,
      mode = EditFormMode.UnboundDrawer,
      canResolve,
      onCloseAction,
      onCreateAction,
      newItemDraft,
    }: {
      entity: RydeUiUnit;
      mode?: EditFormMode;
      canResolve?: TCanResolve;
      onCloseAction?: () => void;
      onCreateAction?: TDrawerOnCreateAction;
      /** What a new record starts with when "New" is chosen; a duplicate starts from its source. */
      newItemDraft?: Partial<IExtendableDto>;
    }): Promise<SimpleResult<IExtendableDto>> => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.NewOrDuplicate,
          onCloseCustomAction: onCloseAction,
          onCreateCustomAction: onCreateAction,
          canResolve,
          entity,
          mode,
          newItemDraft,
        },
      });
    },
  );

  const openBookingProduction = useEvent(
    ({
      bookingsIds,
      direction,
    }: {
      bookingsIds: number[];
      direction?: DirectionEnum;
    }) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.BookingsProduction,
          bookingsIds,
          direction,
        },
      });
    },
  );
  const openCoursesCalendarRideProduceDrawer = useEvent(
    ({
      slots,
      createSlotRide,
      onAfterChange,
      shouldUseSharedProduceFlow,
    }: {
      slots: ICoursesCalendarSlotDto[];
      createSlotRide?: (slot: ICoursesCalendarSlotDto) => Promise<number>;
      onAfterChange?: () => void;
      shouldUseSharedProduceFlow?: boolean;
    }) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.CoursesCalendarRideProduce,
          slots,
          createSlotRide,
          onAfterChange,
          shouldUseSharedProduceFlow,
        },
      });
    },
  );

  const openRideEditFormDrawer = useEvent(
    ({
      id,
      idCopyFrom,
      onCloseAction,
      duplicateReverseStations,
    }: {
      id?: number;
      idCopyFrom?: number;
      onCloseAction?: () => void;
      duplicateReverseStations?: boolean;
    } = {}) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.RideEditForm,
          onCloseCustomAction: onCloseAction,
          mode: RideCrudModeKey.formMode,
          idCopyFrom,
          id,
          duplicateReverseStations,
        },
      });
    },
  );

  const openJourneyEditFormDrawer = useEvent(
    ({
      id,
      idCopyFrom,
      onCloseAction,
    }: {
      id?: number;
      idCopyFrom?: number;
      onCloseAction?: () => void;
    } = {}) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.JourneyEditForm,
          onCloseCustomAction: onCloseAction,
          mode: EditFormMode.UnboundDrawer,
          idCopyFrom,
          id,
        },
      });
    },
  );

  const openCourseEditFormDrawer = useEvent(
    ({
      id,
      idCopyFrom,
      mode = EditFormMode.UnboundDrawer,
      onCloseAction,
      hasPrefilledDraft,
    }: {
      id?: number;
      idCopyFrom?: number;
      mode?: EditFormMode;
      onCloseAction?: () => void;
      hasPrefilledDraft?: boolean;
    }) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.CourseEditForm,
          onCloseCustomAction: onCloseAction,
          mode,
          idCopyFrom,
          id,
          hasPrefilledDraft,
        },
      });
    },
  );

  const openStationRouteEditFormDrawer = useEvent(
    ({
      id,
      idCopyFrom,
      mode = EditFormMode.UnboundDrawer,
      onCloseAction,
      rideOpenerId,
    }: {
      id?: number;
      idCopyFrom?: number;
      mode?: EditFormMode;
      onCloseAction?: () => void;
      rideOpenerId?: number;
    }) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.StationRouteEditForm,
          onCloseCustomAction: onCloseAction,
          mode,
          idCopyFrom,
          id,
          rideOpenerId,
        },
      });
    },
  );

  const openBookingEditFormDrawer = useEvent(
    ({
      id,
      idCopyFrom,
      mode = EditFormMode.UnboundDrawer,
      onCloseAction,
    }: {
      id?: number;
      idCopyFrom?: number;
      mode?: EditFormMode;
      onCloseAction?: () => void;
    }) => {
      return openAsyncDrawer({
        drawerProps: {
          type: DrawerType.BookingEditForm,
          onCloseCustomAction: onCloseAction,
          mode,
          idCopyFrom,
          id,
        },
      });
    },
  );

  const openCourseSchedulesPreviewDrawer = useEvent(
    ({
      schedules,
      title,
    }: {
      schedules: ICourseScheduleMetricItem[];
      title?: string;
    }) => {
      return openDrawer({
        drawerProps: {
          type: DrawerType.CourseSchedulesPreview,
          schedules,
          title,
        },
      });
    },
  );

  const closeDrawer = useEvent((drawerTypeToClose: DrawerType) => {
    setDrawers(oldDrawers => {
      const lastIndex = oldDrawers.findLastIndex(
        ({ drawerProps: { type } }) => type === drawerTypeToClose,
      );

      if (lastIndex > -1) {
        return oldDrawers.toSpliced(lastIndex, 1);
      }

      return oldDrawers;
    });
  });

  const getDrawer = useEvent((drawerType: DrawerType) => {
    const lastIndex = drawers.findLastIndex(
      ({ drawerProps: { type } }) => type === drawerType,
    );

    if (lastIndex > -1) {
      return drawers[lastIndex];
    }

    return undefined;
  });

  return {
    openDrawer,
    clearCrudEditDrawerAtomsBeforeOpen,
    closeDrawer,
    getDrawer,
    drawers,
    openEditFormDrawer,
    openAddDropdownFormDrawer,
    openNewOrDuplicateDrawer,
    openBookingProduction,
    openCoursesCalendarRideProduceDrawer,
    openAsyncDrawer,
    openRideEditFormDrawer,
    openCourseEditFormDrawer,
    openCourseSchedulesPreviewDrawer,
    openBookingEditFormDrawer,
    openStationRouteEditFormDrawer,
    openJourneyEditFormDrawer,
  };
};
